import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBoxFull } from '@fortawesome/pro-regular-svg-icons/faBoxFull'
import { InstantSearch, Configure, useHits } from 'react-instantsearch'
import { useProductSearch } from '../../utils/useProductSearch'
import isObjectLike from 'lodash/isObjectLike'
import sortBy from 'lodash/sortBy'
import { isInStorefront } from '../../utils/isInStorefront'

let Icon = () => (
    <div className="flex items-center justify-center w-full py-10">
        <FontAwesomeIcon icon={faBoxFull} size="6x" />
    </div>
)

const ProductCard = ({
    product,
    isEditMode,
    productCardDirection = 'column',
    hitsDirection,
    fontColour,
    fontFamily,
    attributes,
    attributeOperator,
    isFirst,
    isLast,
    index,
    fallbackImage,
    ...restProps
}) => {
    const images = product.resources?.filter(r => r.type === 'image')
    const firstImage = images ? sortBy(images, ['displayOrder'])[0] : null

    return (
        <div
            className={`
            flex flex-col mb-0 flex-[1] product-card
                ${
                    hitsDirection === 'column'
                        ? 'ml-0'
                        : index % 2 === 0
                        ? '-ml-[0.5rem] mr-[0.5rem]'
                        : '-mr-[0.5rem]'
                }
                ${
                    index % 2 === 0
                        ? 'md:-ml-[0.5rem] md:mr-[0.5rem]'
                        : 'md:ml-[0.5rem] md:-mr-[0.5rem]'
                }
                ${
                    hitsDirection === 'row'
                        ? isFirst
                            ? 'lg:-ml-[0.5rem]'
                            : 'lg:ml-[0.5rem]'
                        : 'lg:ml-0'
                }
                ${
                    hitsDirection === 'row'
                        ? isLast
                            ? 'lg:-mr-[0.5rem]'
                            : 'lg:mr-[0.5rem]'
                        : 'lg-ml-0'
                }
            `}
            data-testid={`product-card`}
            style={{
                ...({
                    ...(productCardDirection === 'column' && {
                        maxWidth: {
                            base: hitsDirection === 'column' ? `100%` : '50%',
                            md: `50%`,
                            lg: `215px`,
                            xl: `268px`,
                        },
                    }),
                },
                {
                    ...(productCardDirection === 'row' && {
                        width: '100%',
                        marginBottom: '30px',
                    }),
                },
                {
                    ...(productCardDirection === 'column' && {
                        width: {
                            base: hitsDirection === 'column' ? `100%` : '50%',
                            md: `50%`,
                            lg: `215px`,
                            xl: `268px`,
                        },
                        flexBasis: {
                            base: hitsDirection === 'column' ? `100%` : '50%',
                            md: `50%`,
                            lg: `215px`,
                            xl: `268px`,
                        },
                    }),
                }),
            }}
            // ml={{
            //     base: hitsDirection === 'column' ? 0 : index % 2 === 0 ? '-0.5rem' : '0.5rem',
            //     md: index % 2 === 0 ? '-0.5rem' : '0.5rem',
            //     lg: hitsDirection === 'row' ? (isFirst ? '-0.5rem' : '0.5rem') : 0,
            // }}
            // mr={{
            //     base: hitsDirection === 'column' ? 0 : index % 2 === 0 ? '0.5rem' : '-0.5rem',
            //     md: index % 2 === 0 ? '0.5rem' : '-0.5rem',
            //     lg: hitsDirection === 'row' ? (isLast ? '-0.5rem' : '0.5rem') : 0,
            // }}
            {...restProps}
        >
            <a
                className="flex"
                style={{
                    flexDirection: productCardDirection,
                    color: fontColour,
                    ...(productCardDirection === 'column' && {
                        height: '100%',
                    }),
                }}
                href={`/products/${product.slug}`}
                onClick={e => {
                    e.preventDefault()
                    if (isEditMode) {
                        return null
                    } else {
                        window.location = `/products/${product.slug}`
                    }
                }}
            >
                <img
                    {...(isObjectLike(product.attributes) && {
                        alt: product.attributes.product_name,
                    })}
                    src={firstImage?.location || fallbackImage || undefined}
                    className={`product-card__media w-full flex-[1] ${
                        productCardDirection === 'row' ? 'max-w-[50%]' : 'max-w-none'
                    }`}
                    data-testid={`product-card__media`}
                />
                <p
                    className="product-card__name font-bold text-[14px] lg:text-[16px] leading-[20px] flex-[1]"
                    style={{
                        marginTop: productCardDirection === 'column' ? `10px` : 0,
                        marginLeft: productCardDirection === 'row' ? `10px` : 0,
                    }}
                    fontFamily={fontFamily}
                    data-testid={`product-card__name`}
                >
                    {isObjectLike(product.attributes) && product.attributes.product_name}
                </p>
            </a>
        </div>
    )
}

const Hits = props => {
    const {
        hits,
        isEditMode,
        productCardDirection,
        hitsDirection,
        fontColour,
        fontFamily,
        fallbackImage,
    } = props

    const inStorefront = isInStorefront()

    return hits.length > 0 ? (
        <>
            {hits.map((hit, index) => (
                <ProductCard
                    key={hit?.slug || `hit_${index}`}
                    product={hit}
                    isEditMode={isEditMode}
                    productCardDirection={productCardDirection}
                    hitsDirection={hitsDirection}
                    fontColour={fontColour}
                    fontFamily={fontFamily}
                    fallbackImage={fallbackImage}
                    index={index}
                    isFirst={index === 0}
                    isLast={index === hits.length - 1}
                />
            ))}
        </>
    ) : isEditMode || !inStorefront ? (
        <Icon />
    ) : null
}
const CustomHits = props => {
    const { items } = useHits()

    return <Hits {...props} hits={items} />
}

const ProductsContent = ({
    numberOfProducts = 4,
    attributes = [],
    attributeOperator = 'and',
    hitsAlignment = 'flex-start',
    hitsDirection = 'row',
    productCardDirection = 'column',
    fontColour = '#000',
    fontFamily = 'body',
    fallbackImage = '',
    isEditMode,
}) => {
    const { hasConfig, searchClient, indexName, configureProps } = useProductSearch({
        attributes,
        attributeOperator,
        numberOfProducts,
    })

    if (!hasConfig) {
        console.error(
            'No Algolia configuration defined. Please refer to the Peracto documentation.'
        )

        return null
    }

    return (
        <InstantSearch searchClient={searchClient} indexName={indexName}>
            <Configure {...configureProps} />

            <CustomHits
                isEditMode={isEditMode}
                productCardDirection={productCardDirection}
                hitsDirection={hitsDirection}
                fontColour={fontColour}
                fontFamily={fontFamily}
                fallbackImage={fallbackImage}
            />
        </InstantSearch>
    )
}

const ProductsRenderer = ({ state, ...props }) => {
    const { hitsDirection = 'row', hitsAlignment } = state
    return (
        <div
            className="flex flex-col flex-wrap product-card-container content__products lg:flex-nowrap"
            style={{
                flexDirection: hitsDirection,
                justifyContent: hitsDirection === 'row' ? hitsAlignment : null,
                alignItems: hitsDirection === 'column' ? hitsAlignment : null,
                padding: hitsDirection === 'row' ? '0.5rem' : 0,
            }}
        >
            <ProductsContent {...state} {...props} />
        </div>
    )
}

export default ProductsRenderer
